/**
 * Mudanças no quadro JÁ NA TELA, antes de o servidor responder
 * (SPEC-20261027-1941-quadro-mais-rapido, pedido do Cristian: "a velocidade de
 * atualização do quadro está ruim").
 *
 * Antes, mover um cartão ou excluir um item só aparecia depois de o quadro
 * inteiro voltar do servidor. Agora a tela muda na hora, e a busca do quadro
 * segue por trás para acertar o que o servidor decide (status, etapa, ordem).
 * Funções puras sobre a visão em cache — sem React, para testar.
 */
type Cartao = { id: string }
type Lane<C extends Cartao> = { id: string; cards: C[] }
type Visao<C extends Cartao> = { columns: Lane<C>[] }

/** Move o cartão para a lane de destino, antes de `antesDe` (ou no fim). */
export function moverCartaoNaVisao<C extends Cartao, V extends Visao<C>>(
  visao: V,
  cartaoId: string,
  laneDestinoId: string,
  antesDe: string | null,
): V {
  let cartao: C | undefined
  for (const lane of visao.columns) {
    cartao ??= lane.cards.find((c) => c.id === cartaoId)
  }
  if (!cartao || !visao.columns.some((l) => l.id === laneDestinoId)) return visao
  const movido = cartao
  return {
    ...visao,
    columns: visao.columns.map((lane) => {
      const sem = lane.cards.filter((c) => c.id !== cartaoId)
      if (lane.id !== laneDestinoId) return sem.length === lane.cards.length ? lane : { ...lane, cards: sem }
      const posicao = antesDe ? sem.findIndex((c) => c.id === antesDe) : -1
      const cards = [...sem]
      cards.splice(posicao >= 0 ? posicao : cards.length, 0, movido)
      return { ...lane, cards }
    }),
  }
}

/** Tira o item (arquivado ou excluído) de todas as lanes. */
export function tirarCartaoDaVisao<C extends Cartao, V extends Visao<C>>(visao: V, cartaoId: string): V {
  if (!visao.columns.some((l) => l.cards.some((c) => c.id === cartaoId))) return visao
  return {
    ...visao,
    columns: visao.columns.map((lane) =>
      lane.cards.some((c) => c.id === cartaoId) ? { ...lane, cards: lane.cards.filter((c) => c.id !== cartaoId) } : lane,
    ),
  }
}

/** A query em cache é a VISÃO de um quadro (['kanban', <id>, 'view']). */
export const ehVisaoDeQuadro = (chave: readonly unknown[]) => chave[0] === 'kanban' && chave[2] === 'view'
